import React, { useEffect, useState, useRef } from 'react';
import { X, Copy, Check, Share2, MessageCircle, Mail, Download, QrCode } from 'lucide-react';
import QRCode from 'qrcode';
import { COLORS } from '../constants';

const ShareModal = ({ isOpen, onClose, propertyTitle, propertyUrl }) => {
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [copied, setCopied] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        if (isOpen && propertyUrl) {
            QRCode.toDataURL(propertyUrl, {
                width: 300,
                margin: 2,
                color: {
                    dark: COLORS.secondary || '#2C353D',
                    light: '#FFFFFF'
                }
            })
            .then(url => {
                setQrCodeUrl(url);
            })
            .catch(err => {
                console.error("Failed to generate QR Code", err);
            });
        }
    }, [isOpen, propertyUrl]);

    // Close on escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(propertyUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy", err);
        }
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: propertyTitle,
                    url: propertyUrl
                });
            } catch (err) {
                console.error("Error sharing", err);
            }
        }
    };

    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`Regarde ce bien : ${propertyTitle} - ${propertyUrl}`)}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(`Bien immobilier : ${propertyTitle}`)}&body=${encodeURIComponent(`Bonjour, voici un bien immobilier qui pourrait t'intéresser : ${propertyTitle}\n\nLien : ${propertyUrl}`)}`;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300">
            <div 
                ref={modalRef}
                className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl border border-gray-100 flex flex-col animate-in fade-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
                    <h3 className="font-black text-[#2C353D] text-lg flex items-center gap-2">
                        <Share2 size={20} className="text-[#EA723D]" /> Partager / QR Code
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col items-center gap-6 overflow-y-auto max-h-[80vh]">
                    <p className="text-sm font-bold text-center text-gray-500 line-clamp-2 px-2">
                        {propertyTitle}
                    </p>

                    {/* QR Code Container */}
                    <div className="relative group p-4 bg-gray-50 rounded-2xl border border-gray-100 shadow-inner flex flex-col items-center justify-center w-64 h-64">
                        {qrCodeUrl ? (
                            <img src={qrCodeUrl} alt="QR Code" className="w-56 h-56 object-contain rounded-lg" />
                        ) : (
                            <div className="w-56 h-56 flex items-center justify-center text-gray-400 text-xs font-medium">
                                Génération du QR Code...
                            </div>
                        )}
                    </div>

                    {/* QR Download Button */}
                    {qrCodeUrl && (
                        <a
                            href={qrCodeUrl}
                            download={`qrcode-${propertyTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`}
                            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white bg-[#EA723D] hover:bg-[#d85e2b] px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0"
                        >
                            <Download size={14} /> Télécharger le QR Code
                        </a>
                    )}

                    {/* Share Action Grid */}
                    <div className="w-full space-y-4">
                        {/* Copy Link field */}
                        <div className="flex items-center gap-2 p-1.5 bg-gray-50 border border-gray-100 rounded-xl">
                            <span className="text-xs text-gray-400 truncate flex-1 pl-2.5 pr-1 font-mono">
                                {propertyUrl}
                            </span>
                            <button
                                onClick={handleCopy}
                                className={`flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-[#2C353D] hover:bg-[#1a2126] text-white'}`}
                            >
                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                {copied ? 'Copié !' : 'Copier'}
                            </button>
                        </div>

                        {/* Social Share Buttons */}
                        <div className="grid grid-cols-2 gap-3">
                            <a
                                href={whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 p-3 border border-gray-100 rounded-xl bg-white hover:bg-gray-50 text-gray-700 text-sm font-bold shadow-sm transition-all hover:scale-[1.01]"
                            >
                                <MessageCircle size={16} className="text-[#25D366]" /> WhatsApp
                            </a>
                            <a
                                href={emailUrl}
                                className="flex items-center justify-center gap-2 p-3 border border-gray-100 rounded-xl bg-white hover:bg-gray-50 text-gray-700 text-sm font-bold shadow-sm transition-all hover:scale-[1.01]"
                            >
                                <Mail size={16} className="text-blue-500" /> Email
                            </a>
                        </div>

                        {/* Native Share fallback if available */}
                        {navigator.share && (
                            <button
                                onClick={handleNativeShare}
                                className="w-full flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-xl bg-white hover:bg-gray-50 text-gray-700 text-sm font-bold shadow-sm transition-all hover:scale-[1.01]"
                            >
                                <Share2 size={16} className="text-[#EA723D]" /> Partager via mobile
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Click outside backdrop handler */}
            <div className="absolute inset-0 -z-10" onClick={onClose}></div>
        </div>
    );
};

export default ShareModal;
